import React, { useMemo, useState } from 'react'

import { useHistory } from 'react-router-dom'

import { useDashboard } from 'redux/hooks/Dashboards'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Text from 'components_new/atoms/Text'

import CardMedia from 'components_new/atoms/Card/CardMedia'

import DashboardTreeSelector from 'components_new/organisms/DashboardTreeSelector'
import LoadingSection from 'components_new/molecules/LoadingSection'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { getThumbnail } from 'utils/functions'
import { addRef } from 'utils/queryHandler'
import { useParams } from 'utils/hooks'

import { Context } from 'types/GlobalUserEvents'
import { useTranslation } from 'language/translator'
import { useDashboardActions } from 'redux/actions/Dashboards'

interface CopyOrMoveDashboardDialogProps {
  id: string | null
  open: boolean
  onClose: () => void
  type: 'copy' | 'move'
}

const useContextText = (type: 'copy' | 'move') => {
  const { t } = useTranslation()

  if (type === 'copy') {
    return t('dashboards', 'select_dashboard_to_copy_tab')
  }

  return (
    <>
      <Text fontSize="inherit" gutterBottom>
        {t('dashboards', 'select_dashboard_to_move_tab')}
      </Text>
      <Text variant="body2" color="inherit">
        {t('dashboards', 'move_tab_warning')}
      </Text>
    </>
  )
}

const CopyOrMoveDashboardDialog = (props: CopyOrMoveDashboardDialogProps) => {
  const { id, onClose, open, type } = props
  const { id: currentDashboardId } = useParams()
  const [loading, setLoading] = useState<boolean>(false)
  const [targetDashboardGroup, setTargetDashboardGroup] = useState<
    string | null
  >(null)
  const history = useHistory()
  const dashboard = useDashboard(id)
  const { t } = useTranslation()
  const { tryCreateDashboard, tryMoveDashboard } = useDashboardActions()

  const contextText = useContextText(type)

  const thumbnail = useMemo(
    () => getThumbnail(dashboard?.thumbnail ?? null),
    [dashboard?.thumbnail]
  )

  const handleCloseDialog = () => {
    onClose()
    setLoading(false)
  }

  const handleCopy = () => {
    if (!targetDashboardGroup || !dashboard) {
      // no target dashboard group selected
      return
    }

    setLoading(true)

    if (type === 'copy') {
      tryCreateDashboard(
        {
          data: {
            dashboard_id: dashboard.id,
            dashboard_group_id: targetDashboardGroup
          }
        },
        (createdId) => {
          handleCloseDialog()
          history.push(addRef(`/dashboards/${createdId}`, Context.DUPLICATE))
        }
      )
    } else {
      tryMoveDashboard(dashboard.id, targetDashboardGroup, (err) => {
        if (err) {
          setLoading(false)
        } else {
          // successfully moved dashboard
          if (currentDashboardId !== dashboard.id) {
            // if the dashboard is not the current dashboard, navigate to it
            history.push(addRef(`/dashboards/${dashboard.id}`, Context.MOVE))
          }

          handleCloseDialog()
        }
      })
    }
  }

  return (
    <SimpleDialog
      actions={
        loading ? null : (
          <>
            <Button
              disabled={loading}
              onClick={handleCloseDialog}
              variant="text"
            >
              {t('global', 'cancel')}
            </Button>
            <Button
              disabled={loading || !dashboard || !targetDashboardGroup}
              onClick={handleCopy}
            >
              {t('global', type === 'copy' ? 'copy_to' : 'move_to')}
            </Button>
          </>
        )
      }
      contentText={loading ? undefined : contextText}
      maxWidth="xs"
      onClose={handleCloseDialog}
      open={open}
      title={t('global', type === 'copy' ? 'copy_to' : 'move_to')}
    >
      <Box
        sx={{
          position: 'relative'
        }}
      >
        {loading ? (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1,
              bgcolor: 'rgba(255, 255, 255, 0.92)'
            }}
          >
            <LoadingSection
              description={[
                t(
                  'dashboards',
                  type === 'copy'
                    ? 'copying_to_dashboard'
                    : 'moving_to_dashboard'
                )
              ]}
              sx={{ py: 2 }}
            />
          </Box>
        ) : null}

        {/* preview of dashboard */}
        <CardMedia
          component="img"
          src={thumbnail}
          sx={{
            height: 240
          }}
        />

        <Collapse in={!loading}>
          <DashboardTreeSelector
            selected={targetDashboardGroup}
            setSelected={setTargetDashboardGroup}
            type="dashboard"
          />
        </Collapse>
      </Box>
    </SimpleDialog>
  )
}

export default CopyOrMoveDashboardDialog
